import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { IonCheckbox, IonSpinner } from '@ionic/angular/standalone';
import { IActionMapping, ITreeOptions, ITreeState, TreeComponent, TreeModule, TreeNode } from '@ali-hm/angular-tree-component';
import { firstValueFrom } from 'rxjs';
import { SubSink } from 'subsink';
import { environment } from '@env/environment';
import { ApiResponse } from '@app/core/models/user.model';
import { TreeAttachment } from '@app/core/interfaces/formly-forms.interface';
import { TreeNodeData } from '@app/core/interfaces/tree.interface';
import { ContextService } from '@app/core/services/context.service';
import { MiscService } from '@app/core/services/misc.service';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';

@Component({
  selector: 'app-formly-tree-select',
  templateUrl: './formly-field-tree-select.component.html',
  styleUrls: ['./formly-field-tree-select.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    TreeModule,
    IonCheckbox,
    IonSpinner,
    DynamicIconComponent,
  ],
})
export class FormlyFieldTreeSelectComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  @ViewChild(TreeComponent) tree!: TreeComponent;

  public nodes: TreeNodeData[] = [];
  public treeState: ITreeState = {};
  public isLoading = true;
  public selectedLeafIds = new Set<string>();
  public treeOptions: ITreeOptions = {
    allowDrag: () => false,
    allowDrop: () => false,
    actionMapping: this.getActionMapping(),
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    nodeHeight: 28,
    levelPadding: 20,
    animateExpand: true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
  };

  private readonly subsink = new SubSink();
  private readonly miscService = inject(MiscService);
  private readonly http = inject(HttpClient);
  private readonly contextService = inject(ContextService);

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subsink.sink = this.formControl.valueChanges.subscribe((value) => {
      this.updateLocalValue(value);
    });

    await this.getSourceData();
    this.updateLocalValue(this.formControl.value);

    if (this.nodes.length > 0) {
      this.treeState.expandedNodeIds = { [this.nodes[0].id]: true };
    }

    this.isLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public get isDisabled(): boolean {
    return !!(this.options.formState.disabled || this.field.props?.['disabled'] || this.field.props?.['readonly']);
  }

  public onNodeCheckboxChange(node: TreeNode, checked: boolean): void {
    if (this.isDisabled) {
      return;
    }

    const leafIds = this.getLeafIdsForNode(node.data as TreeNodeData);
    for (const leafId of leafIds) {
      if (checked) {
        this.selectedLeafIds.add(leafId);
      } else {
        this.selectedLeafIds.delete(leafId);
      }
    }

    this.updateFormControl();
  }

  public isNodeChecked(node: TreeNode): boolean {
    const leafIds = this.getLeafIdsForNode(node.data as TreeNodeData);
    if (leafIds.length === 0) {
      return false;
    }

    return leafIds.every((leafId) => this.selectedLeafIds.has(leafId));
  }

  public isNodeIndeterminate(node: TreeNode): boolean {
    if (this.isLeafNode(node)) {
      return false;
    }

    const leafIds = this.getLeafIdsForNode(node.data as TreeNodeData);
    if (leafIds.length === 0) {
      return false;
    }

    const selectedCount = leafIds.filter((leafId) => this.selectedLeafIds.has(leafId)).length;
    return selectedCount > 0 && selectedCount < leafIds.length;
  }

  public getSelectedLeafPathsAsText(): string {
    if (this.nodes.length === 0 || this.selectedLeafIds.size === 0) {
      return '';
    }

    const root = this.nodes[0];
    return Array.from(this.selectedLeafIds)
      .map((leafId) => this.miscService.getNodePath(root, leafId).join(' / '))
      .filter((path) => !!path)
      .join(', ');
  }

  public expandRootNode(): void {
    if (this.nodes.length > 0) {
      this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
      this.treeState.expandedNodeIds = { ...this.treeState.expandedNodeIds, [this.nodes[0].id]: true };
      this.changeDetectorRef.markForCheck();
    }
  }

  private updateLocalValue(value: unknown): void {
    const normalized = Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
    this.selectedLeafIds = new Set(normalized);
    this.changeDetectorRef.markForCheck();
  }

  private updateFormControl(): void {
    const selectedLeafIds = Array.from(this.selectedLeafIds);
    this.formControl.setValue(selectedLeafIds);
    this.formControl.markAsDirty();
    this.onChange(selectedLeafIds);
    this.changeDetectorRef.markForCheck();
  }

  private async getSourceData(): Promise<void> {
    const configuredTreeData = this.field.props['treeData'];
    if (configuredTreeData) {
      this.nodes = Array.isArray(configuredTreeData) ? configuredTreeData as TreeNodeData[] : [configuredTreeData as TreeNodeData];
      return;
    }

    const treeAttachmentConfig = this.field?.props?.['treeAttachment'] as TreeAttachment;
    if (!treeAttachmentConfig) {
      console.warn('No treeAttachment configuration found for tree select field.');
      return;
    }

    const clientId = this.contextService.getCurrentClientId();
    const subsidiaryId = this.contextService.getCurrentSubsidiaryId();
    if (!clientId || !subsidiaryId) {
      console.error('Missing clientId or subsidiaryId in context service.');
      return;
    }

    const url = treeAttachmentConfig.sourceApi
      .replace('{{apiUrl}}', environment.apiUrl)
      .replace('{{clientId}}', clientId)
      .replace('{{subsidiaryId}}', subsidiaryId);

    const response = await firstValueFrom(this.http.get<ApiResponse<unknown>>(url));
    if (!response?.success || !response.data) {
      console.error('Failed to load tree data from source API.');
      return;
    }

    let tree = this.miscService.getDeepChildValue(response.data, treeAttachmentConfig.targetIdField);
    if (!tree) {
      console.error(`Target ID field "${treeAttachmentConfig.targetIdField}" not found in API response data.`);
      return;
    }

    if (treeAttachmentConfig.chRoot) {
      try {
        const obj = eval(treeAttachmentConfig.chRoot.object);
        const chRoot = obj ? obj[treeAttachmentConfig.chRoot.prop] : null;
        if (chRoot) {
          tree = this.miscService.findNodeInTree(tree, chRoot);
        }
      } catch (error) {
        console.error('Error evaluating chRoot object expression:', error);
        return;
      }
    }

    this.nodes = Array.isArray(tree) ? tree as TreeNodeData[] : [tree as TreeNodeData];
  }

  private getActionMapping(): IActionMapping {
    return {
      mouse: {
        click: () => {
          return;
        },
      },
    };
  }

  private isLeafNode(node: TreeNode): boolean {
    const hasChildren = typeof (node as any).hasChildren === 'function'
      ? (node as any).hasChildren()
      : (node?.data?.children?.length || node?.children?.length) > 0;
    return !hasChildren;
  }

  private getLeafIdsForNode(node: TreeNodeData): string[] {
    if (!node.children || node.children.length === 0) {
      return [node.id];
    }

    return node.children.flatMap((child) => this.getLeafIdsForNode(child));
  }
}